import { z } from "zod/v4";
import { formaters } from "vincent-utils";
import { QuoteTrackingSchema, QuoteStatusHistoryEntrySchema, QuoteRealizationSchema, QuoteRealizationLineSchema, } from "./zodSchemas/QuoteTrackingSchemas.js";
// Protobuf JSON omits default scalars, empty arrays and absent optionals.
// These adapters restore the legacy UI/tRPC shape, without any business rules.
const historySchema = QuoteStatusHistoryEntrySchema.extend({
    effectiveDate: z.string().nullable().default(null),
    previousDeliveryDate: z.string().nullable().default(null),
    deliveryDate: z.string().nullable().default(null),
    previousAcceptanceDate: z.string().nullable().default(null),
    acceptanceDate: z.string().nullable().default(null),
});
const trackingSchema = QuoteTrackingSchema.extend({
    deliveryDate: z.string().nullable().default(null),
    acceptanceDate: z.string().nullable().default(null),
    history: z.array(historySchema).default([]),
});
const realizationSchema = QuoteRealizationSchema.extend({
    totalLines: z.number().default(0),
    performedLines: z.number().default(0),
    ambiguousLines: z.number().default(0),
    unverifiableLines: z.number().default(0),
    completionDate: z.string().nullable().default(null),
    withoutRecordedAcceptance: z.boolean().default(false),
    lines: z
        .array(QuoteRealizationLineSchema.extend({
        description: z.string().default(""),
        ccamCode: z.string().nullable().default(null),
        teeth: z.array(z.string()).default([]),
        acteId: z.string().nullable().default(null),
        performedDate: z.string().nullable().default(null),
        competingQuoteIds: z.array(z.string()).default([]),
    }))
        .default([]),
});
const lineSchema = z.object({
    id: z.string(),
    description: z.string().default(""),
    ccamCode: z.string().nullable().default(null),
    teeth: z.array(z.string()).default([]),
    fees: z.string(),
    base: z.string(),
    amo: z.string(),
    tx: z.number().default(0),
    materials: z.string().default(""),
    isAlternative: z.boolean().default(false),
    rangPr: z.number().nullable().default(null),
    rangPt: z.number().nullable().default(null),
    index: z.number().default(0),
});
const patientQuotesSchema = z.object({
    quotes: z
        .array(z.object({
        id: z.string(),
        name: z.string().default(""),
        creationDate: z.iso.date(),
        deliveryDate: z.string().nullable().default(null),
        state: z.string(),
        lines: z.array(lineSchema).default([]),
        tracking: trackingSchema,
        realization: realizationSchema.optional(),
    }))
        .default([]),
});
export function quoteTrackingFromRpc(value) {
    return trackingSchema.parse(value);
}
export function patientQuotesFromRpc(value) {
    return patientQuotesSchema.parse(value).quotes.map((quote) => ({
        id: quote.id,
        name: quote.name,
        state: quote.state,
        creation_date: new Date(`${quote.creationDate}T00:00:00Z`),
        delivery_date: quote.deliveryDate
            ? new Date(`${quote.deliveryDate}T00:00:00Z`)
            : null,
        tracking: quote.tracking,
        realization: quote.realization,
        lines: quote.lines.map((line) => ({
            id: line.id,
            description: line.description,
            ccam_code: line.ccamCode,
            teeth: line.teeth,
            fees: line.fees,
            base: line.base,
            amo: line.amo,
            tx: line.tx,
            materials: line.materials,
            is_alternative: line.isAlternative,
            rang_pr: line.rangPr,
            rang_pt: line.rangPt,
            index: line.index,
        })),
    }));
}
const count = z.number().int().nonnegative().default(0);
const listSchema = z.object({
    rows: z
        .array(z.object({
        id: z.string(),
        patientId: z.string(),
        patientName: z.string().default(""),
        patientForename: z.string().default(""),
        name: z.string().default(""),
        creationDate: z.iso.date(),
        totalFees: z.string(),
        tracking: trackingSchema,
        realization: realizationSchema.optional(),
    }))
        .default([]),
    total: count,
    pageSize: z.number(),
    stats: z.object({
        counts: z.record(z.string(), z.number()).default({}),
        delivered: count,
        accepted: count,
        acceptedFromRealization: count,
        acceptanceRate: z.number().nullable().default(null),
        abandonedAfterAcceptance: count,
        missingDeliveryDate: count,
        excludedInvalid: count,
    }),
    realizationStats: z.object({
        performed: count,
        partial: count,
        ambiguous: count,
        withoutRecordedAcceptance: count,
    }),
});
export function quoteTrackingListFromRpc(value) {
    const result = listSchema.parse(value);
    return {
        ...result,
        rows: result.rows.map((row) => {
            const patient = formaters.formatNameForenameForDisplay({
                name: row.patientName,
                forename: row.patientForename,
            });
            return {
                ...row,
                patientName: `${patient.name ?? ""} ${patient.forename ?? ""}`.trim(),
                creationDate: new Date(`${row.creationDate}T00:00:00Z`),
            };
        }),
    };
}
//# sourceMappingURL=QuoteRpc.js.map